import * as THREE from "three";
import type { AlignmentData } from "../types.ts";
import type { FloorPlan } from "../viewer/FloorPlan.ts";
import { kabsch2d } from "../utils/kabsch2d.ts";

/**
 * Pair-picking tool for aligning the RoomPlan layer to the RTABmap mesh.
 *
 * Workflow:
 *   1. User clicks a point on the 3D mesh      → stores (x, z) in world space.
 *   2. User clicks the matching spot on the SVG → stores (x, z) in plan space.
 *   3. Repeat ≥ 2 times (3 recommended for good RMSE).
 *   4. `compute()` runs 2D Kabsch and returns yaw + translation.
 *
 * State transitions are exposed via `onStateChange` so the UI layer can update
 * hint text without polling.
 */

export type AlignStep = "pick_mesh" | "pick_plan";

export interface AlignPair {
  /** World XZ position on the RTABmap mesh. */
  rtab: [number, number];
  /** Absolute XZ position in the RoomPlan plan (metres). */
  room: [number, number];
}

const MARKER_COLORS = [0x4aff6a, 0x4a9eff, 0xffaa4a, 0xff4aa8, 0xaaff4a];

export class AlignTool {
  private active = false;
  private pendingRtab: [number, number] | null = null;
  private raycaster = new THREE.Raycaster();
  private markerGroup = new THREE.Group();

  pairs: AlignPair[] = [];
  onStateChange: ((step: AlignStep, count: number) => void) | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.Camera,
    private scene: THREE.Scene,
    private floorPlan: FloorPlan,
    private meshesRef: () => THREE.Mesh[],
  ) {
    this.markerGroup.name = "align_markers";
    this.scene.add(this.markerGroup);
  }

  activate(): void {
    if (this.active) return;
    this.active = true;
    this.pendingRtab = null;
    this.pairs = [];
    this._clearMarkers();
    this.canvas.style.cursor = "crosshair";
    this.canvas.addEventListener("click", this._onCanvasClick, true);
    this.floorPlan.clickMode = "align";
    this.floorPlan.onAlignPick = this._onPlanPick;
    this._notify();
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    this.canvas.style.cursor = "";
    this.canvas.removeEventListener("click", this._onCanvasClick, true);
    this.floorPlan.clickMode = "teleport";
    this.floorPlan.onAlignPick = undefined;
    this.pendingRtab = null;
    this._clearMarkers();
  }

  removeLastPair(): void {
    if (this.pendingRtab) {
      this.pendingRtab = null;
      this._popMarker();
    } else if (this.pairs.length) {
      this.pairs.pop();
      this._popMarker();
    } else {
      return;
    }
    this._notify();
  }

  reset(): void {
    this.pendingRtab = null;
    this.pairs = [];
    this._clearMarkers();
    this._notify();
  }

  /** Returns null if fewer than 2 pairs are collected. */
  compute(): (AlignmentData & { rmse: number }) | null {
    if (this.pairs.length < 2) return null;
    const src = this.pairs.map((p) => p.room);
    const tgt = this.pairs.map((p) => p.rtab);
    const r = kabsch2d(src, tgt);
    return {
      target: "rtabmap",
      source: "roomplan",
      yaw: r.yaw,
      tx: r.tx,
      tz: r.tz,
      rmse: r.rmse,
      rmse_meters: r.rmse,
      method: "manual_2d",
    };
  }

  get currentStep(): AlignStep {
    return this.pendingRtab === null ? "pick_mesh" : "pick_plan";
  }

  dispose(): void {
    this.deactivate();
    this._clearMarkers();
    this.scene.remove(this.markerGroup);
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _onCanvasClick = (e: MouseEvent): void => {
    if (!this.active) return;
    const hit = this._raycast(e);
    if (!hit) return;

    e.stopPropagation();
    e.preventDefault();

    // If a pending 3D pick already exists (user hasn't clicked the plan yet),
    // replace it — lets the user fix a misclick without resetting everything.
    if (this.pendingRtab !== null) {
      this._popMarker();
    }
    this.pendingRtab = [hit.x, hit.z];
    this._addMeshMarker(hit);
    this._notify();
  };

  private _onPlanPick = (planX: number, planZ: number): void => {
    if (!this.active || this.pendingRtab === null) return;
    this.pairs.push({ rtab: this.pendingRtab, room: [planX, planZ] });
    this.pendingRtab = null;
    // Marker stays in the scene — it already marks this pair.
    this._notify();
  };

  private _raycast(e: MouseEvent): THREE.Vector3 | null {
    const rect = this.canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.meshesRef(), true);
    if (!hits.length) return null;
    return hits[0].point.clone();
  }

  private _addMeshMarker(pos: THREE.Vector3): void {
    const color = MARKER_COLORS[this.pairs.length % MARKER_COLORS.length];
    const geo = new THREE.SphereGeometry(0.08, 16, 12);
    const mat = new THREE.MeshBasicMaterial({ color, depthTest: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.renderOrder = 1000;
    this.markerGroup.add(mesh);
  }

  private _popMarker(): void {
    const last = this.markerGroup.children[this.markerGroup.children.length - 1] as THREE.Mesh | undefined;
    if (!last) return;
    this.markerGroup.remove(last);
    last.geometry?.dispose();
    (last.material as THREE.Material)?.dispose?.();
  }

  private _clearMarkers(): void {
    while (this.markerGroup.children.length) {
      const m = this.markerGroup.children[0] as THREE.Mesh;
      this.markerGroup.remove(m);
      m.geometry?.dispose();
      (m.material as THREE.Material)?.dispose?.();
    }
  }

  private _notify(): void {
    this.onStateChange?.(this.currentStep, this.pairs.length);
  }
}
